import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import type { ShallowRef } from 'vue';

interface ComposableApiOptions<T extends AxiosResponse> {
  immediate?: boolean
  initialData?: any
  resetToInitialOnExecute?: boolean
  executeOnConfigChange?: boolean
  onSuccess?: (response: T) => Promise<T> | void
  onError?: (error: unknown) => Promise<AxiosError> | void
  onFinish?: () => void
}

export interface UseApiReturnContext<T extends AxiosResponse> {
  execute: (AxiosRequestConfig?: AxiosRequestConfig<T['data']>) => Promise<T | AxiosError>
  isLoading: Ref<boolean>
  data: ShallowRef<T['data'] | null>
  error: Ref<AxiosError | null>
}

// TODO: научиться обрабатывать requestConfig как реактивный
export function useApi<T extends AxiosResponse, D = T['data']>(
  instance: AxiosInstance,
  endpointUrl: string,
  requestConfig?: AxiosRequestConfig<D> | Ref<AxiosRequestConfig<D>>,
  options: ComposableApiOptions<T> = {}
): UseApiReturnContext<T> & PromiseLike<UseApiReturnContext<T>> {
  const { immediate, initialData = null, onSuccess, onError, onFinish, resetToInitialOnExecute, executeOnConfigChange } = options;

  const isLoading = ref(false);
  const data = shallowRef<T['data'] | null>(initialData);
  const error = shallowRef<AxiosError | null>(null);

  let lastEndpointExecuteId = 0;
  const execute = async (currentExecuteConfig?: typeof requestConfig) => {
    isLoading.value = true;
    error.value = null;
    resetToInitialOnExecute && (data.value = initialData);

    const currentEndpointExecuteId = ++lastEndpointExecuteId;
    return instance<D, T, D>(endpointUrl, unref(currentExecuteConfig) ?? unref(requestConfig))
      .then((response) => {
        data.value = response.data;

        return onSuccess?.(response) || Promise.resolve(response);
      })
      .catch((axiosError) => {
        error.value = axiosError;

        return onError?.(axiosError) || Promise.reject(axiosError);
      })
      .finally(() => {
        onFinish?.();

        if (currentEndpointExecuteId === lastEndpointExecuteId) {
          isLoading.value = false;
        }
      });
  };

  const shell = {
    execute,
    isLoading,
    data,
    error,
  };

  if (executeOnConfigChange) {
    watch(requestConfig && (isRef(requestConfig) || isReactive(requestConfig)) ? requestConfig : computed(() => requestConfig), () => execute(), {
      immediate,
    });
  }
  else if (immediate) {
    execute();
  }

  return {
    ...shell,

    then: (onfulfilled, onrejected) => {
      let shellResultPromise;
      if (immediate) {
        shellResultPromise = new Promise<UseApiReturnContext<T>>((resolve) => {
          const stopWatch = watch(isLoading, (value) => {
            if (value === false) {
              stopWatch();

              resolve(shell);
            }
          });
        });
      }
      else {
        shellResultPromise = Promise.resolve(shell);
      }

      return shellResultPromise.then(onfulfilled, onrejected);
    },
  };
}
